import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../components/AuthWrapper';
import { Navigate, Link } from 'react-router-dom';
import { 
  collection, addDoc, doc, updateDoc, deleteDoc, getDocs
} from 'firebase/firestore';
import { db } from '../../utils/firebase';

const AdminTimeline = () => {
  const { isAdmin } = useContext(AuthContext);
  const [timelineData, setTimelineData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    date: '',
    title: '',
    description: '',
    category: '',
    sources: '',
    tags: ''
  });

  // Load timeline data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get timeline data from Firestore
        const querySnapshot = await getDocs(collection(db, 'timeline'));
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTimelineData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error loading timeline data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEditClick = (item) => {
    setEditItem(item);
    setFormData({
      id: item.id,
      date: item.date || '',
      title: item.title || '',
      description: item.description || '',
      category: item.category || '',
      sources: Array.isArray(item.sources) ? item.sources.join(', ') : '',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Add new timeline entry
  const handleSaveNew = async (e) => {
    e.preventDefault();
    
    // Prepare new data
    const newItem = {
      ...formData,
      sources: formData.sources ? formData.sources.split(',').map(s => s.trim()) : [],
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
    };
    
    try {
      // Add document to Firestore
      const docRef = await addDoc(collection(db, 'timeline'), newItem);
      
      // Update local state with the new ID from Firestore
      setTimelineData([...timelineData, {
        ...newItem,
        id: docRef.id
      }]);
      
      // Reset form
      setEditItem(null);
      setFormData({
        id: '',
        date: '',
        title: '',
        description: '',
        category: '',
        sources: '',
        tags: ''
      });
      
      alert('New timeline entry added successfully!');
    } catch (error) {
      console.error('Error adding timeline entry:', error);
      alert('Error adding entry: ' + error.message);
    }
  };

  // Update existing timeline entry
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare updated data
    const updatedItem = {
      ...formData,
      sources: formData.sources ? formData.sources.split(',').map(s => s.trim()) : [],
      tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : []
    };
    
    try {
      // Update document in Firestore
      const docRef = doc(db, 'timeline', updatedItem.id);
      await updateDoc(docRef, updatedItem);
      
      // Update local state
      const updatedData = timelineData.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      );
      setTimelineData(updatedData);
      
      // Reset form
      setEditItem(null);
      setFormData({
        id: '',
        date: '',
        title: '',
        description: '',
        category: '',
        sources: '',
        tags: ''
      });
      
      alert('Timeline entry updated successfully!');
    } catch (error) {
      console.error('Error updating timeline entry:', error);
      alert('Error updating entry: ' + error.message);
    }
  };

  // Delete timeline entry
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this timeline entry?')) {
      try {
        // Delete document from Firestore
        await deleteDoc(doc(db, 'timeline', id));
        
        // Update local state
        const updatedData = timelineData.filter(item => item.id !== id);
        setTimelineData(updatedData);
        
        alert('Timeline entry deleted successfully!');
      } catch (error) {
        console.error('Error deleting timeline entry:', error);
        alert('Error deleting entry: ' + error.message);
      }
    }
  };

  const handleAddNew = () => {
    // Generate a unique ID
    const newId = `timeline-${Date.now()}`;
    
    setEditItem(null);
    setFormData({
      id: newId,
      date: new Date().toISOString().split('T')[0],
      title: '',
      description: '',
      category: '',
      sources: '',
      tags: ''
    });
  };

  // Check if user is admin
  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-navy">Timeline Management</h1>
        <div className="space-x-4">
          <button 
            onClick={handleAddNew}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
          >
            Add New Entry
          </button>
          <Link 
            to="/admin"
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-8">Loading timeline data...</div>
      ) : (
        <div className="grid grid-cols-1 gap-8">
          {/* Edit Form */}
          {(editItem || formData.id) && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">
                {editItem ? 'Edit Timeline Entry' : 'Add New Timeline Entry'}
              </h2>
              <form onSubmit={editItem ? handleSubmit : handleSaveNew}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">-- Select Category --</option>
                      <option value="legal">Legal</option>
                      <option value="medical">Medical</option>
                      <option value="incident">Incident</option>
                      <option value="correspondence">Correspondence</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    rows="4"
                    required
                  ></textarea>
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sources (comma separated)</label>
                  <input
                    type="text"
                    name="sources"
                    value={formData.sources}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. Exhibit A, Police Report, Medical Record"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded"
                    placeholder="e.g. arrest, evidence, medical"
                  />
                </div>
                
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setEditItem(null);
                      setFormData({
                        id: '',
                        date: '',
                        title: '',
                        description: '',
                        category: '',
                        sources: '',
                        tags: ''
                      });
                    }}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                  >
                    {editItem ? 'Update Entry' : 'Add Entry'}
                  </button>
                </div>
              </form>
            </div>
          )}
          
          {/* Timeline Entries List */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {timelineData.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap">{item.date}</td>
                    <td className="px-6 py-4">{item.title}</td>
                    <td className="px-6 py-4">{item.category || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEditClick(item)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTimeline;